import { StanButton } from '@/components';
import { cn } from '@/lib';
import { useEffect, useMemo, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { HexColorPicker } from 'react-colorful';
import { tv } from 'tailwind-variants';
import { Button } from '../../../ui/Button';
import { Icon } from '../../../ui/Icon';
import { isValidHex } from './color.logic';
import './react-colorful.scss';

interface Props {
    id: string;
    hex: string;
    label: string;
    description?: string;
    active?: boolean;
    className?: string;
    onClick?: (id: string) => void;
    onHexChange?: (hex: string, id: string) => void;
}

export const Color = ({ id, hex, label, description, onClick, active = false, onHexChange, className }: Props) => {
    const [resetHex, setHexReset] = useState(hex);
    const [inputValue, setInputValue] = useState(hex.replace('#', ''));
    const { getCollapseProps } = useCollapse({ isExpanded: active });

    // Sync input value when hex prop changes externally (e.g., from color picker)
    useEffect(() => {
        setInputValue(hex.replace('#', ''));
    }, [hex]);

    useEffect(() => {
        if (active) setHexReset(hex);
    }, [active]);

    const variants = tv({
        base: 'border-neutral-subtle body-sm flex w-full flex-col items-center justify-between rounded-md border overflow-hidden transition-colors duration-100',
        variants: {
            active: {
                true: 'border-border-brand bg-[var(--bg-color-subtle)]',
                false: '',
            },
        },
    });

    const isValidColor = useMemo(() => isValidHex(`#${inputValue}`), [inputValue]);

    return (
        <div className={variants({ active, class: className })}>
            <div
                className='hover:bg-bg-subtle flex w-full cursor-pointer flex-nowrap items-center gap-3 p-3 transition-colors duration-100'
                onClick={() => onClick?.(id)}
            >
                <span
                    className='block aspect-square w-[40px] flex-shrink-0 rounded-md border border-black/10'
                    style={{ backgroundColor: hex }}
                />
                <div className='flex w-full flex-1 flex-col items-start'>
                    <span className='text-stan-950 font-bold'>{label}</span>
                    <span className='text-grey-50'>{description}</span>
                </div>

                {!active ? (
                    <Icon name='chevron' className={cn('-rotate-90')} />
                ) : (
                    <StanButton name='confirm-edit-color' className='!min-w-0' size='sm'>
                        Done
                    </StanButton>
                )}
            </div>
            <div {...getCollapseProps()} className='flex w-full transform-gpu flex-col'>
                <div className='p-3'>
                    <div className='h-[120px]'>
                        {active && (
                            <HexColorPicker
                                className='w-full transform-gpu'
                                color={hex}
                                onChange={hex => onHexChange?.(hex, id)}
                            />
                        )}
                    </div>
                    <div className='mt-2 flex flex-nowrap items-center justify-between'>
                        <div
                            className={cn(
                                'bg-stan-100 inline-flex h-fit w-26 flex-shrink-0 flex-nowrap gap-2 overflow-hidden rounded-md border border-solid px-3 py-2',
                                !isValidColor ? 'border-red-400' : 'border-transparent'
                            )}
                        >
                            <span className='text-grey-50 inline-flex flex-shrink-0'>#</span>
                            <input
                                name='hex'
                                value={inputValue}
                                className='text-stan-950 flex w-full p-0 uppercase'
                                maxLength={6}
                                onChange={e => {
                                    const value = e.target.value.replace(/[^0-9A-Fa-f]/g, '');
                                    const fullHex = `#${value}`;
                                    setInputValue(value);

                                    if (isValidHex(fullHex)) {
                                        onHexChange?.(fullHex, id);
                                    }
                                }}
                                onBlur={() => {
                                    if (!isValidHex(inputValue)) setInputValue(hex.replace('#', ''));
                                }}
                            />
                        </div>
                        <Button
                            onClick={() => {
                                setInputValue(resetHex.replace('#', ''));
                                onHexChange?.(resetHex, id);
                            }}
                        >
                            Reset
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
